/**
 * №22.3 (D-CH1) → №22.3.2 (F1) — the App-Shell scope resolver. It was shared by the
 * bottom bar and the docked panel; §3 ② deleted the bar, so its one reader is now the
 * work rail's conversation (`CoPanel`, where the stream lives) — which is also the
 * chat SUBJECT's, since those are the same mount re-parented. "Here" therefore means
 * the same thing in the rail and on the stage, by construction rather than by care.
 *
 * №22.3.2 moved the resolution itself into `route-anchor-matrix.ts` — a committed,
 * gated table of every routed surface → its anchor, chip, chat scope and page context.
 * What survives here is the actor mirror and the chip/avatar helpers the chrome renders.
 */
import { resolveSurface, type ResolvedSurface, type RouteAnchor } from "./route-anchor-matrix";
import type { StreamActor, StreamAnchorKind } from "../../../lib/types";

export type { RouteAnchor };

/**
 * The resolved surface, in the shape the chrome consumed before the matrix landed
 * (`ctx.scope` / `ctx.page` / `ctx.placeholder`). One row, four consumers.
 */
export interface RouteContext {
  chip: string;
  scope: ResolvedSurface["scope"];
  page: ResolvedSurface["page"];
  placeholder: string;
}

/**
 * №22.3.1 (D-AF4) — the client MIRROR of the server's `resolveDefaultActor` (pure
 * core in `src/engine/collaboration.ts`), so the "with {actor}" label + composer
 * to-line resolve BEFORE the anchor stream responds. The two must agree; the server
 * value is authoritative and overrides this once the stream loads. Kept in lock-step
 * with the pure core (asserted there); this exists only to kill the first-paint flash.
 */
export const WORKSPACE_ACTOR_NAME = "Kuvio";

export function defaultActorForAnchor(
  anchorKind: StreamAnchorKind,
  ctx: { hire?: { id: string; name: string } | null; taskAgent?: { id: string; name: string } | null; rootId?: string | null },
): StreamActor {
  const workspaceActor: StreamActor = { kind: "agent", id: ctx.rootId ?? null, name: WORKSPACE_ACTOR_NAME, is_workspace: true };
  switch (anchorKind) {
    case "agent":
      return ctx.hire?.id ? { kind: "agent", id: ctx.hire.id, name: ctx.hire.name || WORKSPACE_ACTOR_NAME } : workspaceActor;
    case "task":
      return ctx.taskAgent?.id ? { kind: "agent", id: ctx.taskAgent.id, name: ctx.taskAgent.name || WORKSPACE_ACTOR_NAME } : workspaceActor;
    default:
      return workspaceActor;
  }
}

/** The actor's avatar chip: initials + colour. The un-personified workspace chat
 *  ("Kuvio") is the dark "K" mark; a named hire takes the accent ramp (D-AF4). */
export function actorAvatar(actor: StreamActor | null | undefined): { initials: string; workspace: boolean } {
  const name = (actor?.name ?? WORKSPACE_ACTOR_NAME).trim() || WORKSPACE_ACTOR_NAME;
  const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0]!).join("").toUpperCase() || "K";
  return { initials, workspace: !!actor?.is_workspace };
}

/** The route's anchor — read off the matrix row (never a hand-written regex). */
export function routeAnchor(pathname: string): RouteAnchor {
  return resolveSurface(pathname).anchor;
}

/** The route's chat context — read off the SAME matrix row as its anchor, which is
 *  what makes "chip == anchor == applied context" structural rather than hopeful. */
export function routeContext(pathname: string): RouteContext {
  const s = resolveSurface(pathname);
  return { chip: s.chip, scope: s.scope, page: s.page, placeholder: s.placeholder };
}
